import type { StorefrontProduct } from '@/features/storefront/api/storefront';

/**
 * The rules a product page reads its variants by: what an axis is, what a
 * colour word looks like, and what a reduction comes to.
 *
 * Apart from the pieces that draw them, because none of it knows what a
 * product page looks like - and because more than one of those pieces needs
 * the same answers. The comparison table under the page paints its swatches
 * from the same word list the buy column picks a fabric with.
 *
 * This began as one theme's file. It is here because the arrangement it serves
 * is the merchant's now rather than that theme's: an option picker with a row
 * per axis is a setting on the options component, offered to every shop, and a
 * component in `features/storefront` may not reach into `previewThemes` for
 * the rule it draws by.
 */

/**
 * The colour words this shop's fabrics, finishes and lacquers are named in.
 *
 * A furniture shop names a variant "Sage Cotton" and "Natural Oak", never
 * `#9caf94`, and the API has nowhere to hang a hex on a variant option - so a
 * theme that wants swatches has to read the merchant's own words. Matched a
 * word at a time and first hit wins, which is why the list holds the colour
 * rather than the material: "Sage Cotton" is sage, and "Oat Linen" is oat.
 *
 * A value naming no colour in here gets no swatch, and the axis it belongs to
 * falls back to labelled buttons - see {@link optionAxes}. That is the honest
 * result for a "2 seat / 3 seat" axis, which is not a colour at all.
 */
const SWATCH_WORDS: Record<string, string> = {
  ash: '#d8cbb6',
  bark: '#5b4636',
  black: '#141414',
  boucle: '#e2ddd2',
  chalk: '#f1efe9',
  charcoal: '#3a3d42',
  cobalt: '#1837bc',
  cotton: '#e8e4dc',
  cream: '#efe7d6',
  espresso: '#4a3428',
  flint: '#8d8f8c',
  gravel: '#9a9894',
  gray: '#9aa0a6',
  grey: '#9aa0a6',
  heritage: '#8a6a45',
  ink: '#232733',
  linen: '#e6ddcd',
  mist: '#cfd8d6',
  natural: '#d9c7a7',
  navy: '#1f2a44',
  oak: '#c8a877',
  oat: '#ddd0b8',
  olive: '#6f7345',
  rust: '#a9502c',
  saffron: '#e0a12c',
  sage: '#9caf94',
  sand: '#ddc9a3',
  storm: '#7c828c',
  teal: '#2f6f6a',
  terracotta: '#c1663f',
  vermilion: '#d8442a',
  walnut: '#6b4a2f',
  white: '#ffffff',
};

/** The colour a variant value names, if it names one at all. */
export function swatchFor(value: string): string | undefined {
  for (const word of value.toLowerCase().split(/[^a-z]+/)) {
    const hit = SWATCH_WORDS[word];
    if (hit) return hit;
  }
  return undefined;
}

/** One axis a product is chosen along: Fabric, Size, Finish. */
export interface OptionAxis {
  name: string;
  values: string[];
  /** Whether every value on this axis names a colour, and so can be a dot. */
  swatches: boolean;
}

/**
 * A product's variants, read back as the axes they were built from.
 *
 * The API hands over the *combinations* - six rows for three fabrics in two
 * sizes - because that is what a cart line points at. A shopper picks along
 * one axis at a time, so this puts the axes back together, keeping the order
 * the merchant declared them in: a shopper chooses the fabric before the size
 * because that is the order the shop wrote them down.
 */
export function optionAxes(variants: StorefrontProduct['variants']): OptionAxis[] {
  const axes = new Map<string, string[]>();

  for (const variant of variants) {
    for (const [name, raw] of Object.entries(variant.options)) {
      const value = String(raw ?? '').trim();
      if (!value) continue;
      const values = axes.get(name) ?? [];
      if (!values.includes(value)) values.push(value);
      axes.set(name, values);
    }
  }

  return [...axes].map(([name, values]) => ({
    name,
    values,
    swatches: values.every((value) => swatchFor(value) !== undefined),
  }));
}

/**
 * The variant a shopper means when they change one axis and leave the rest
 * alone.
 *
 * Their current choice is the starting point, so picking "Ink Cotton" from
 * "Oat Linen, 3 seat" lands on "Ink Cotton, 3 seat" rather than resetting the
 * size. Where that exact combination does not exist - a fabric woven only in
 * the two-seater - the nearest one that does is taken, preferring one that is
 * actually in stock over one that is not.
 */
export function variantFor(
  variants: StorefrontProduct['variants'],
  current: StorefrontProduct['variants'][number] | undefined,
  name: string,
  value: string,
): StorefrontProduct['variants'][number] | undefined {
  const wanted = { ...(current?.options ?? {}), [name]: value };
  const agreement = (variant: StorefrontProduct['variants'][number]) =>
    Object.entries(wanted).filter(([axis, want]) => String(variant.options[axis] ?? '') === want)
      .length;

  return variants
    .filter((variant) => String(variant.options[name] ?? '') === value)
    .sort((a, b) => agreement(b) - agreement(a) || Number(b.in_stock) - Number(a.in_stock))[0];
}

/**
 * What a reduction comes to, as the whole number a badge says.
 *
 * Null rather than zero where there is nothing to announce, so a badge is
 * hidden by the absence of a saving rather than by every caller remembering to
 * check for one. The strings are read as numbers here and never written back -
 * see `lib/money` for why money stays a string everywhere else.
 */
export function savedPercent(price: string, compareAt: string | null): number | null {
  const now = Number(price);
  const was = Number(compareAt ?? '');
  if (!Number.isFinite(now) || !Number.isFinite(was) || was <= now || was <= 0) return null;
  return Math.round(((was - now) / was) * 100);
}
